"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.LuxonDateTimeFormatStringSchema = exports.LuxonDateTimeFormatStringRegex = exports.Reporting = exports.RecurringDate = exports.NonBusinessDayAdjustmentSchema = exports.DaySchema = exports.DayIterable = void 0;
const zod_1 = require("zod");
const Util = __importStar(require("./Utils"));
// Week starts with Monday (1) and ends with Sunday (7).
const WeekdaySchema = zod_1.z.union([
    zod_1.z.literal(1),
    zod_1.z.literal(2),
    zod_1.z.literal(3),
    zod_1.z.literal(4),
    zod_1.z.literal(5),
    zod_1.z.literal(6),
    zod_1.z.literal(7),
]);
exports.DayIterable = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];
exports.DaySchema = zod_1.z.enum(exports.DayIterable);
const DayUnitSchema = zod_1.z.enum(['businessDay', 'calendarDay']);
exports.NonBusinessDayAdjustmentSchema = zod_1.z.enum(['before', 'after']);
var RecurringDate;
(function (RecurringDate) {
    // ====== Building Blocks =======
    // ====== Offset Operation =======
    // offset by calendar days or business days
    const AbstractDateOffsetSchema = zod_1.z.object({
        days: zod_1.z.number(),
        dayUnit: DayUnitSchema,
    });
    const BusinessDateOffsetSchema = AbstractDateOffsetSchema.extend({
        dayUnit: zod_1.z.literal('businessDay'),
    });
    const CalendarDateOffsetSchema = AbstractDateOffsetSchema.extend({
        dayUnit: zod_1.z.literal('calendarDay'),
        nonBusinessDayAdjustment: zod_1.z.optional(exports.NonBusinessDayAdjustmentSchema),
    });
    const DateOffsetSchema = zod_1.z.union([
        BusinessDateOffsetSchema,
        CalendarDateOffsetSchema,
    ]);
    /**
     * ### Valid Base Date Config Types
     *
     * - types in order of which type is used more frequently
     * - no daily because weekly can do all days of week if all selected
     */
    const BaseDateConfigTypeSchema = zod_1.z.enum([
        'weekday',
        'calendarDate',
        'startOf',
        'endOf',
        'weekdayByOccurrence',
    ]);
    const FrequencyPeriodSchema = zod_1.z.enum(['week', 'month', 'quarter', 'year']);
    const ReccuringFrequencyPeriodSchema = zod_1.z.enum(['month', 'quarter', 'year']);
    const AbstractBaseDateConfigSchema = zod_1.z.object({
        type: BaseDateConfigTypeSchema,
        cadence: zod_1.z.number(),
        offset: zod_1.z.optional(DateOffsetSchema),
    });
    // ====== Start of/ End of period =======
    const AbstractStartOrEndOfPeriodRecurringDateConfigSchema = AbstractBaseDateConfigSchema.extend({
        type: zod_1.z.enum(['startOf', 'endOf']),
        period: ReccuringFrequencyPeriodSchema,
    });
    /**
     * ### Start of Period Recurring Date Config
     *
     * Uses the `startDate` to calculate the start of the period (and month if quarter or year)
     *
     * - `Offset` is applied to the calculated start of the period
     *
     * - `Cadence` is applied to the calculated start of the period
     *
     * - Calculated dates falling outside of the range [`startDate`, `endDate`] are omitted
     * #### _Example:_
     *
     * Template:
     * [configType] [period], once every [cadence] [period] for dates ranging between [startDate] to [endDate]
     *
     * Example Case:
     * [**Start of**] [**month**], once every [**2**] [**months**] ranging between [**1/15/2020**] to [**5/1/2020**]
     *
     * ```js
     * {
     *   type: BaseDateConfigType.EndOf,
     *   startDate: '2020-01-15',
     *   endDate: '2020-05-01',
     *   period: 'month',
     *   cadence: 2,
     * }
     * ```
     */
    const AbstractStartOfPeriodRecurringDateConfigSchema = AbstractStartOrEndOfPeriodRecurringDateConfigSchema.extend({
        type: zod_1.z.literal('startOf'),
        period: ReccuringFrequencyPeriodSchema,
    });
    const StartOfPeriodRecurringDateConfigSchema = AbstractStartOfPeriodRecurringDateConfigSchema.extend({
        startDate: Util.ZodFullDateStrForJsonSchemaStruct,
        endDate: Util.ZodFullDateStrForJsonSchemaStruct,
    });
    /**
     * ### End of Period Recurring Date Config
     *
     * Uses the `startDate` to calculate the end of the period (and month if quarter or year)
     *
     * - `Offset` is applied to the calculated start of the period
     *
     * - `Cadence` is applied to the calculated start of the period
     *
     * - Calculated dates falling outside of the range [`startDate`, `endDate`] are omitted
     *
     * #### _Example:_
     *
     * Template:
     * [offset] [configType] [period], once every [cadence] [period] for dates ranging between [startDate] to [endDate]
     *
     * Example Case:
     * [1 calendar day after] [**End of**] [**month**], once every [**2**] [**months**] ranging between [**1/15/2020**] to [**5/1/2020**]
     *
     * ```js
     * {
     *   type: BaseDateConfigType.EndOf,
     *   startDate: '2020-01-15',
     *   endDate: '2020-05-01',
     *   period: 'month',
     *   cadence: 2,
     *   offset: {
     *     dayUnit: 'calendarDay',
     *     days: 1,
     *   }
     * }
     * ```
     */
    const AbstractEndOfPeriodRecurringDateConfigSchema = AbstractStartOrEndOfPeriodRecurringDateConfigSchema.extend({
        type: zod_1.z.literal('endOf'),
        period: ReccuringFrequencyPeriodSchema,
    });
    const EndOfPeriodRecurringDateConfigSchema = AbstractEndOfPeriodRecurringDateConfigSchema.extend({
        startDate: Util.ZodFullDateStrForJsonSchemaStruct,
        endDate: Util.ZodFullDateStrForJsonSchemaStruct,
    });
    // ====== Calendar Date =======
    /**
     * ### Calendar Date Config
     *
     * Uses the `startDate` to iterate over a set `cadence` of `period` until the `endDate` is reached
     *
     * #### _Example:_
     *
     * Template:
     * [offset] [startDate.fragment], once every [cadence] [period] on [startDate.fragment] for dates ranging between [startDate] to [endDate]
     *
     * Example Case:
     * [1 business day before] [**the 15th**], once every [**2**] [**months**] on ranging between [**1/15/2020**] to [**5/1/2020**]
     *
     * ```js
     * {
     *   type: BaseDateConfigType.CalendarDate,
     *   startDate: '2020-01-15',
     *   endDate: '2020-05-01',
     *   period: 'month',
     *   cadence: 2,
     *   offset: {
     *     dayUnit: 'businessDay',
     *     days: -1,
     *   }
     * }
     * ```
     *
     * Example Case:
     * [1 business day before] [**January 15th**], once every [**2**] [**years**] ranging between [**1/15/2020**] to [**5/1/2020**]
     *
     * ```js
     * {
     *   type: BaseDateConfigType.CalendarDate,
     *   startDate: '2020-01-15',
     *   endDate: '2020-05-01',
     *   period: 'year',
     *   cadence: 2,
     *   offset: {
     *     dayUnit: 'businessDay',
     *     days: -1,
     *   }
     * }
     * ```
     */
    const AbstractCalendarDateConfigSchema = AbstractBaseDateConfigSchema.extend({
        type: zod_1.z.literal('calendarDate'),
        period: ReccuringFrequencyPeriodSchema,
    });
    const CalendarDateConfigSchema = AbstractCalendarDateConfigSchema.extend({
        startDate: Util.ZodFullDateStrForJsonSchemaStruct,
        endDate: Util.ZodFullDateStrForJsonSchemaStruct,
    });
    // ====== Weekday =======
    const BaseWeekdayConfigSchema = AbstractBaseDateConfigSchema.extend({
        type: zod_1.z.enum(['weekday', 'weekdayByOccurrence']),
        period: zod_1.z.union([zod_1.z.literal('week'), ReccuringFrequencyPeriodSchema]),
        weekdays: zod_1.z.array(WeekdaySchema),
    });
    const AbstractWeekdayConfigSchema = BaseWeekdayConfigSchema.extend({
        type: zod_1.z.literal('weekday'),
        period: zod_1.z.literal('week'),
    });
    const WeekdayConfigSchema = AbstractWeekdayConfigSchema.extend({
        startDate: Util.ZodFullDateStrForJsonSchemaStruct,
        endDate: Util.ZodFullDateStrForJsonSchemaStruct,
    });
    // ====== Relative Occurrence by Weekday =======
    const AbstractRelativeWeekdayOccurrenceConfigSchema = BaseWeekdayConfigSchema.extend({
        type: zod_1.z.literal('weekdayByOccurrence'),
        period: ReccuringFrequencyPeriodSchema,
        occurrence: zod_1.z.number(),
    });
    const RelativeWeekdayOccurrenceConfigSchema = AbstractRelativeWeekdayOccurrenceConfigSchema.extend({
        startDate: Util.ZodFullDateStrForJsonSchemaStruct,
        endDate: Util.ZodFullDateStrForJsonSchemaStruct,
    });
    /** Fixed Recurring Date Config (these all require startDate and endDate) */
    const BaseRecurringDateConfigSchema = zod_1.z.union([
        StartOfPeriodRecurringDateConfigSchema,
        EndOfPeriodRecurringDateConfigSchema,
        CalendarDateConfigSchema,
        WeekdayConfigSchema,
        RelativeWeekdayOccurrenceConfigSchema,
    ]);
    const BaseDateConfigSchema = zod_1.z.union([
        BaseRecurringDateConfigSchema,
        zod_1.z.array(Util.ZodFullDateStrForJsonSchemaStruct),
    ]);
    /** Relative configs that DO NOT require start/end dates & instead rely on a "triggerDate" */
    RecurringDate.BaseRelativeRecurringDateConfigSchema = zod_1.z.union([
        AbstractStartOfPeriodRecurringDateConfigSchema,
        AbstractEndOfPeriodRecurringDateConfigSchema,
        AbstractCalendarDateConfigSchema,
        AbstractWeekdayConfigSchema,
        AbstractRelativeWeekdayOccurrenceConfigSchema,
    ]);
    RecurringDate.DateVariableSchema = zod_1.z.object({
        name: zod_1.z.string(),
        definition: zod_1.z.string().optional(),
    });
    RecurringDate.RelativeDateConfigSchema = zod_1.z.object({
        startDateFormula: zod_1.z.string(),
        endDateFormula: zod_1.z.string(),
        // Example: { name: 'triggerDate', definition: 'Trigger Date' }
        // Only key is required, which then converts to an object structure used in formula evaluation: const variables = { [key: string]: value }
        dateVariables: zod_1.z.array(RecurringDate.DateVariableSchema).optional(),
        baseDateConfig: RecurringDate.BaseRelativeRecurringDateConfigSchema,
    });
    // ====== Configs =======
    /**
     * This config will utilize the "baseDateConfig" to create recurring dates that occur between `startDate` and `endDate`. An optional offset will be applied to every `baseDate` that is calculated from the `baseDateConfig`. If there is no offset, the `baseDate` will be returned from the date calculation.
     *
     * These configs can then be used as building blocks to create an array of dates that might have different configurations
     *
     * The `exclude` property can be used configured to exclude any `baseDateConfig+offset` rule you want to exclude from the final set of dates
     */
    RecurringDate.RelativeConfigSchema = zod_1.z.object({
        relativeRecurringDateConfig: zod_1.z.array(RecurringDate.RelativeDateConfigSchema),
        exclusions: zod_1.z.array(RecurringDate.RelativeDateConfigSchema),
    });
    RecurringDate.ConfigSchema = zod_1.z.object({
        recurringDateConfigs: zod_1.z.array(BaseDateConfigSchema),
        exclusions: zod_1.z.array(BaseDateConfigSchema),
    });
})(RecurringDate || (exports.RecurringDate = RecurringDate = {}));
var Reporting;
(function (Reporting) {
    // all resolved start and end dates are inclusive
    Reporting.PeriodIterable = [
        // resolves to start=reportingDate; end=reportingDate
        // reportingDate 2023-10-23 => start: 2023-10-23; end: 2023-10-23;
        'calendarDay',
        // resolves to start=1st day of calendar week; end=last day of calendar week
        // optional weekStartDay parameter to set start day of week, defaults to Monday
        // reportingDate 2023-10-24 (Tuesday) => start: 2023-10-23 (Monday); end: 2023-10-29 (Sunday);
        'calendarWeek',
        // resolves to start=1st day of calendar month; end=last day of calendar month
        // reportingDate 2023-01-16 => start: 2023-01-01; end: 2023-01-31;
        'calendarMonth',
        // resolves to start=1st day of calendar quarter; end=last day of calendar quarter
        // calendar quarters: Q1=1/1-3/31; Q2=4/1-6/30; Q3=7/1-9/30; Q4=10/1-12/31
        // reportingDate 2023-01-16 => start: 2023-01-01; end: 2023-03-31;
        'calendarQuarter',
        // resolves to start=1st day of calendar year; end=last day of calendar year
        // calendar year: 1/1-12/31
        // reportingDate 2023-01-16 => start: 2023-01-01; end: 2023-12-31;
        'calendarYear',
    ];
    // we construct ReportingPeriod data structure from encoded formula engine string
    // for use within LoanTapeMetadata framework. When adding new reportingPeriod properties
    // please update this const and LoanTapeMetadata framework.
    Reporting.AllReportingPeriodPropertiesIterable = [
        'period',
        'weekStartDay',
    ];
    // Zod schemas for ReportingPeriod discriminated union
    Reporting.CalendarDaySchema = zod_1.z.object({
        period: zod_1.z.literal('calendarDay'),
    });
    Reporting.CalendarWeekSchema = zod_1.z.object({
        period: zod_1.z.literal('calendarWeek'),
        weekStartDay: exports.DaySchema.optional(),
    });
    Reporting.CalendarMonthSchema = zod_1.z.object({
        period: zod_1.z.literal('calendarMonth'),
    });
    Reporting.CalendarQuarterSchema = zod_1.z.object({
        period: zod_1.z.literal('calendarQuarter'),
    });
    Reporting.CalendarYearSchema = zod_1.z.object({
        period: zod_1.z.literal('calendarYear'),
    });
    Reporting.ReportingPeriodSchema = zod_1.z.discriminatedUnion('period', [
        Reporting.CalendarDaySchema,
        Reporting.CalendarWeekSchema,
        Reporting.CalendarMonthSchema,
        Reporting.CalendarQuarterSchema,
        Reporting.CalendarYearSchema,
    ]);
})(Reporting || (exports.Reporting = Reporting = {}));
exports.LuxonDateTimeFormatStringRegex = /^(?:\s|'.*'|\W)*(?:S|SSS|u|uu|uuu|s|ss|m|mm|h|hh|H|HH|Z|ZZ|ZZZ|ZZZZ|ZZZZZ|z|a|d|dd|c|ccc|cccc|ccccc|L|M|LL|MM|LLL|MMM|LLLL|MMMM|LLLLL|MMMMM|y|yy|yyyy|G|GG|GGGGG|kk|kkkk|W|WW|o|ooo|q|qq|D|DD|DDD|DDDD|t|tt|ttt|tttt|T|TT|TTT|TTTT|f|ff|fff|ffff|F|FF|FFF|FFFF|X|x)+(?:S|SSS|u|uu|uuu|s|ss|m|mm|h|hh|H|HH|Z|ZZ|ZZZ|ZZZZ|ZZZZZ|z|a|d|dd|c|ccc|cccc|ccccc|L|M|LL|MM|LLL|MMM|LLLL|MMMM|LLLLL|MMMMM|y|yy|yyyy|G|GG|GGGGG|kk|kkkk|W|WW|o|ooo|q|qq|D|DD|DDD|DDDD|t|tt|ttt|tttt|T|TT|TTT|TTTT|f|ff|fff|ffff|F|FF|FFF|FFFF|X|x|\s|'.*'|\W)*$/;
/**
 * What's allowed for this input:
 * - any combination of [Luxon's DateTime formating tokens (click me for the full list)](https://moment.github.io/luxon/#/formatting?id=table-of-tokens)
 * - whitespace(s)
 * - any string surrounded by single quotes (e.g. `'hello'-MM-dd-yyyy'world'`)
 * - any non-word character (e.g. `!@#$%^&*()_+-/<>?:"{}[]\|;`)
 */
exports.LuxonDateTimeFormatStringSchema = zod_1.z
    .string()
    .regex(exports.LuxonDateTimeFormatStringRegex);
//# sourceMappingURL=Date.js.map